import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

interface AccordionProps {
  /** Header text, shown on the left. */
  title: ReactNode
  /** Optional leading icon. */
  icon?: ReactNode
  /** Optional adornment pinned to the right, just before the chevron — e.g. a
   *  count badge (EyeMed) or a summary chip (VSP). */
  aside?: ReactNode
  /** Hide `aside` while expanded — for asides the body repeats (VSP summary). */
  hideAsideWhenOpen?: boolean
  /** Render content edge-to-edge (no padding) — for full-bleed tables (EyeMed). */
  flush?: boolean
  /** Uncontrolled initial state. Ignored when `open` is provided. */
  defaultOpen?: boolean
  /** Controlled open state — pass with `onToggle` for a single-open group
   *  (EyeMed opens one category at a time). */
  open?: boolean
  onToggle?: () => void
  /** Scroll the section into view when it opens after mount — for a
   *  single-open group, where the section that was open above it collapses in
   *  the same update and would otherwise leave the clicked header above the
   *  viewport (EyeMed: scroll down Routine's long list, click Medical). */
  keepInView?: boolean
  children: ReactNode
}

/**
 * Collapsible section shared by the vision slideouts (EyeMed + VSP). Owns the
 * open/close behavior, chevron, and chrome; the header (icon / title / aside)
 * and the body are slotted. Works uncontrolled (`defaultOpen`) or controlled
 * (`open` + `onToggle`). The medical slideout's DeductibleAccordion is
 * intentionally kept separate and does not use this.
 */
export default function Accordion({
  title,
  icon,
  aside,
  hideAsideWhenOpen = false,
  flush = false,
  defaultOpen = false,
  open: controlledOpen,
  onToggle,
  keepInView = false,
  children,
}: AccordionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const handleToggle = () => {
    if (isControlled) onToggle?.()
    else setInternalOpen(prev => !prev)
  }

  // After the commit that opened this section (and collapsed its sibling),
  // before paint: `nearest` leaves a header that is still on screen where it
  // is and brings one that moved above the viewport to the top.
  const rootRef = useRef<HTMLDivElement>(null)
  const wasOpen = useRef(open)
  useLayoutEffect(() => {
    if (keepInView && open && !wasOpen.current) {
      rootRef.current?.scrollIntoView?.({ block: 'nearest' })
    }
    wasOpen.current = open
  }, [keepInView, open])

  return (
    <div className="accordion" ref={rootRef}>
      <button
        type="button"
        className="accordion__toggle"
        onClick={handleToggle}
        aria-expanded={open}
      >
        {icon && <span className="accordion__icon">{icon}</span>}
        <span className="accordion__title">{title}</span>
        {aside && !(hideAsideWhenOpen && open) && (
          <span className="accordion__aside">{aside}</span>
        )}
        <svg
          className={`accordion__chevron${open ? ' accordion__chevron--open' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className={`accordion__content${flush ? '' : ' accordion__content--padded'}`}>
          {children}
        </div>
      )}
    </div>
  )
}
